import {
  SvgAnalytics,
  SvgAttachMoney,
  SvgCancel,
  SvgChecklist,
  SvgMoneyOff,
  SvgPriceCheck,
  SvgReceiptLong,
} from "~/components/Icon/icons";
import { Box } from "~/components/Box";
import { Card } from "~/components/Card";
import { Flex } from "~/components/Flex";
import { Grid } from "~/components/Grid";
import { Skeleton } from "~/components/Skeleton";
import { SummaryResponse } from "../types";
import { asCount, asCurrency, asPercent } from "../utils";
import { KPICard } from "./KPICard";

export interface KPICardsProps {
  summary?: SummaryResponse;
  isFetching: boolean;
}

// 7 cards: 2-up on tablet (2/2/2/1) then 4-up on desktop (4/3) so no lone
// card is left dangling on a wide row.
const GRID_CSS = {
  gap: "$4",
  gridTemplateColumns: "repeat(1, 1fr)",
  "@sm": { gridTemplateColumns: "repeat(2, 1fr)" },
  "@lg": { gridTemplateColumns: "repeat(4, 1fr)" },
} as const;

const SKELETON_COUNT = 7;

const KPICardSkeleton = () => (
  <Card css={{ p: "$5" }}>
    <Flex justify="between" align="start" gap="3">
      <Box css={{ minWidth: 0, flex: 1 }}>
        <Skeleton variant="text-2" css={{ width: "60%" }} />
        <Skeleton variant="text-6" css={{ width: "80%", mt: "$2" }} />
        <Skeleton variant="text-2" css={{ width: "50%", mt: "$2" }} />
      </Box>
      <Skeleton variant="icon-lg" />
    </Flex>
  </Card>
);

export const KPICards = ({ summary, isFetching }: KPICardsProps) => {
  if (isFetching || !summary) {
    return (
      <Grid css={GRID_CSS}>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <KPICardSkeleton key={`kpi-skeleton-${index}`} />
        ))}
      </Grid>
    );
  }

  const { compliance } = summary;

  return (
    <Grid css={GRID_CSS}>
      <KPICard
        label="Gasto Total"
        value={asCurrency(summary.totalSpent)}
        iconName={SvgAttachMoney}
      />
      <KPICard
        label="Total de Pedidos Emitidos"
        value={asCount(summary.totalOrders)}
        iconName={SvgReceiptLong}
      />
      <KPICard
        label="Cancelamentos Após Emissão"
        value={asCount(summary.totalCancellations)}
        iconName={SvgCancel}
        hint={`${asPercent(summary.cancellationRate)} dos pedidos emitidos`}
      />
      <KPICard
        label="Economia Desperdiçada"
        value={asCurrency(summary.wastedSavings)}
        iconName={SvgMoneyOff}
      />
      <KPICard
        label="Conformidade Menor Tarifa"
        value={asPercent(compliance?.lowerFare?.rate)}
        iconName={SvgPriceCheck}
        hint={`${asCount(compliance?.lowerFare?.violated)} violações de ${asCount(
          compliance?.lowerFare?.total,
        )}`}
      />
      <KPICard
        label="Conformidade Valor Fixo"
        value={asPercent(compliance?.fixedValue?.rate)}
        iconName={SvgChecklist}
        hint={`${asCount(compliance?.fixedValue?.violated)} violações de ${asCount(
          compliance?.fixedValue?.total,
        )}`}
      />
      <KPICard
        label="Conformidade Antecedência"
        value={asPercent(compliance?.leadTime?.rate)}
        iconName={SvgAnalytics}
        hint={`${asCount(compliance?.leadTime?.violated)} violações de ${asCount(
          compliance?.leadTime?.total,
        )}`}
      />
    </Grid>
  );
};

KPICards.displayName = "KPICards";
